import { useState, useEffect } from 'react';
import {
  Shield,
  Search,
  Filter,
  Calendar,
  User,
  Activity,
  FileText,
  MessageSquare,
  Users,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Download,
  Eye,
  Clock
} from 'lucide-react';
import { UserRole } from '../types';

interface AuditLog {
  id: string;
  action_type: string;
  target_type: string;
  target_id: string;
  details: any;
  created_at: string;
  first_name: string;
  last_name: string;
  email: string;
  role: UserRole;
}

interface AuditFilters {
  action_type: string;
  target_type: string;
  admin_id: string;
  page: number;
  limit: number;
}

interface DetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  log: AuditLog | null;
}

function DetailsModal({ isOpen, onClose, log }: DetailsModalProps) {
  if (!isOpen || !log) return null;

  const formatDetails = (details: any) => {
    if (typeof details === 'string') {
      try {
        return JSON.parse(details);
      } catch {
        return details;
      }
    }
    return details;
  };

  const details = formatDetails(log.details);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-96 overflow-hidden">
        <div className="flex items-center p-6 bg-gray-50 border-b rounded-t-lg">
          <Shield className="w-6 h-6 text-blue-400" />
          <h3 className="ml-3 text-lg font-semibold text-gray-900">
            Λεπτομέρειες Ενέργειας Διαχειριστή
          </h3>
        </div>
        <div className="p-6 overflow-y-auto max-h-80">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Ενέργεια:</span>
              <span className="text-sm text-gray-900">{getActionLabel(log.action_type)}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Στόχος:</span>
              <span className="text-sm text-gray-900">{getTargetLabel(log.target_type)}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Διαχειριστής:</span>
              <span className="text-sm text-gray-900">
                {log.first_name} {log.last_name} ({log.email})
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Ημερομηνία:</span>
              <span className="text-sm text-gray-900">
                {new Date(log.created_at).toLocaleString('el-GR')}
              </span>
            </div>
            
            {details && (
              <div>
                <span className="text-sm font-medium text-gray-600 block mb-2">Λεπτομέρειες:</span>
                <div className="bg-gray-50 rounded-lg p-3">
                  <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                    {JSON.stringify(details, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end p-6 bg-gray-50 border-t rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Κλείσιμο
          </button>
        </div>
      </div>
    </div>
  );
}

function getActionLabel(actionType: string): string {
  const labels: { [key: string]: string } = {
    'role_change': 'Αλλαγή Ρόλου',
    'user_activated': 'Ενεργοποίηση Χρήστη',
    'user_deactivated': 'Απενεργοποίηση Χρήστη',
    'user_deleted': 'Διαγραφή Χρήστη',
    'case_deleted': 'Διαγραφή Περιστατικού',
    'forum_post_deleted': 'Διαγραφή Δημοσίευσης',
    'user_suspended': 'Αναστολή Χρήστη',
    'user_reactivated': 'Επαναφορά Χρήστη',
    'case_resolved': 'Επίλυση Περιστατικού',
    'compliance_report_generated': 'Δημιουργία Αναφοράς Συμμόρφωσης'
  };
  return labels[actionType] || actionType;
}

function getTargetLabel(targetType: string): string {
  const labels: { [key: string]: string } = {
    'user': 'Χρήστης',
    'case': 'Περιστατικό',
    'forum_post': 'Δημοσίευση Φόρουμ',
    'forum_reply': 'Απάντηση Φόρουμ',
    'system': 'Σύστημα'
  };
  return labels[targetType] || targetType;
}

function getActionIcon(actionType: string) {
  switch (actionType) {
    case 'role_change':
    case 'user_activated':
    case 'user_deactivated':
    case 'user_suspended':
    case 'user_reactivated':
      return <Users className="w-4 h-4" />;
    case 'user_deleted':
      return <AlertTriangle className="w-4 h-4" />;
    case 'case_deleted':
    case 'case_resolved':
      return <FileText className="w-4 h-4" />;
    case 'forum_post_deleted':
      return <MessageSquare className="w-4 h-4" />;
    case 'compliance_report_generated':
      return <Shield className="w-4 h-4" />;
    default:
      return <Activity className="w-4 h-4" />;
  }
}

function getActionColor(actionType: string): string {
  switch (actionType) {
    case 'user_activated':
    case 'user_reactivated':
    case 'case_resolved':
      return 'text-green-600 bg-green-100';
    case 'user_deleted':
    case 'case_deleted':
    case 'forum_post_deleted':
      return 'text-red-600 bg-red-100';
    case 'user_deactivated':
    case 'user_suspended':
      return 'text-yellow-600 bg-yellow-100';
    case 'role_change':
      return 'text-blue-600 bg-blue-100';
    case 'compliance_report_generated':
      return 'text-purple-600 bg-purple-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
}

export function AuditLogs() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  const [filters, setFilters] = useState<AuditFilters>({
    action_type: '',
    target_type: '',
    admin_id: '',
    page: 1,
    limit: 50
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 1,
    hasNext: false,
    hasPrevious: false
  });

  useEffect(() => {
    fetchAuditLogs();
  }, [filters]);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      
      const queryParams = new URLSearchParams();
      if (filters.action_type) queryParams.set('action_type', filters.action_type);
      if (filters.target_type) queryParams.set('target_type', filters.target_type);
      if (filters.admin_id) queryParams.set('admin_id', filters.admin_id);
      queryParams.set('page', filters.page.toString());
      queryParams.set('limit', filters.limit.toString());

      const response = await fetch(`/api/v1/admin/audit-logs?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Αποτυχία λήψης αρχείων καταγραφής');
      }

      const result = await response.json();
      setLogs(result.data.logs);
      setPagination(result.data.pagination);
    } catch (error) {
      console.error('Fetch audit logs error:', error);
      setError(error instanceof Error ? error.message : 'Άγνωστο σφάλμα');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const openDetailsModal = (log: AuditLog) => {
    setSelectedLog(log);
    setShowDetailsModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-medical-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <XCircle className="w-5 h-5 text-red-400 mr-2" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Σφάλμα φόρτωσης</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
        <button
          onClick={fetchAuditLogs}
          className="mt-4 bg-red-100 hover:bg-red-200 text-red-800 px-4 py-2 rounded-md text-sm font-medium transition-colors"
        >
          Δοκιμή ξανά
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Αρχεία Καταγραφής</h1>
            <p className="text-gray-600 mt-1">
              Παρακολούθηση {pagination.total.toLocaleString()} ενεργειών διαχειριστών
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={fetchAuditLogs}
              className="flex items-center px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Ανανέωση
            </button>
            <button className="flex items-center px-3 py-2 bg-medical-600 hover:bg-medical-700 text-white rounded-md transition-colors">
              <Download className="w-4 h-4 mr-2" />
              Εξαγωγή
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Τύπος Ενέργειας
            </label>
            <select
              value={filters.action_type}
              onChange={(e) => setFilters(prev => ({ ...prev, action_type: e.target.value, page: 1 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Όλες οι ενέργειες</option>
              <option value="role_change">Αλλαγή Ρόλου</option>
              <option value="user_activated">Ενεργοποίηση Χρήστη</option>
              <option value="user_deactivated">Απενεργοποίηση Χρήστη</option>
              <option value="user_deleted">Διαγραφή Χρήστη</option>
              <option value="case_deleted">Διαγραφή Περιστατικού</option>
              <option value="forum_post_deleted">Διαγραφή Δημοσίευσης</option>
              <option value="case_resolved">Επίλυση Περιστατικού</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Τύπος Στόχου
            </label>
            <select
              value={filters.target_type}
              onChange={(e) => setFilters(prev => ({ ...prev, target_type: e.target.value, page: 1 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Όλοι οι στόχοι</option>
              <option value="user">Χρήστης</option>
              <option value="case">Περιστατικό</option>
              <option value="forum_post">Δημοσίευση Φόρουμ</option>
              <option value="system">Σύστημα</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ανά σελίδα
            </label>
            <select
              value={filters.limit}
              onChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ action_type: '', target_type: '', admin_id: '', page: 1, limit: 50 })}
              className="w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
            >
              Καθαρισμός Φίλτρων
            </button>
          </div>
        </div>
      </div>

      {/* Audit Logs Timeline */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="divide-y divide-gray-200">
          {logs.map((log) => (
            <div key={log.id} className="p-6 hover:bg-gray-50 transition-colors">
              <div className="flex items-start space-x-4">
                <div className={`flex-shrink-0 p-2 rounded-full ${getActionColor(log.action_type)}`}>
                  {getActionIcon(log.action_type)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {getActionLabel(log.action_type)}
                      </p>
                      <p className="text-sm text-gray-500">
                        {getTargetLabel(log.target_type)} • ID: {log.target_id}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => openDetailsModal(log)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded"
                        title="Προβολή λεπτομερειών"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      <User className="w-4 h-4 mr-1" />
                      {log.first_name} {log.last_name}
                    </div>
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {new Date(log.created_at).toLocaleString('el-GR')}
                    </div>
                  </div>
                  
                  {log.details && typeof log.details === 'object' && (
                    <div className="mt-2">
                      <div className="text-xs text-gray-600 bg-gray-50 rounded p-2">
                        {Object.entries(log.details).slice(0, 2).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="font-medium">{key}:</span>
                            <span className="truncate ml-2">{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.hasPrevious}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Προηγούμενη
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.hasNext}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Επόμενη
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Εμφάνιση{' '}
                  <span className="font-medium">
                    {((pagination.page - 1) * pagination.limit) + 1}
                  </span>{' '}
                  έως{' '}
                  <span className="font-medium">
                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span>{' '}
                  από{' '}
                  <span className="font-medium">{pagination.total}</span> αποτελέσματα
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={!pagination.hasPrevious}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Προηγούμενη
                  </button>
                  
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    const page = Math.max(1, Math.min(
                      pagination.page - 2 + i,
                      pagination.pages - 4 + i
                    ));
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === pagination.page
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.hasNext}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Επόμενη
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Details Modal */}
      <DetailsModal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedLog(null);
        }}
        log={selectedLog}
      />
    </div>
  );
}